import { database } from '../firebase';
import { ref, get } from 'firebase/database';
import { notifApi } from '../config/apiClient';
import firebaseDataService from './FirebaseDataService';
import { taskDataService } from './taskDataService';

// Bildirim gönderme durumu için tip tanımı
export interface NotificationStatus {
  success: boolean;
  message: string;
  sentCount?: number;
  errorCount?: number;
}

// Bildirim tipi
export interface Notification {
  id: string;
  title: string;
  body: string;
  createdAt: number;
  recipients: string[];
  status: 'pending' | 'sent' | 'failed';
  sentCount?: number;
  errorCount?: number;
  sentAt?: number;
  read?: string[]; // Bildirimi okuyan kullanıcıların ID'leri
  readAt?: number; // Bildirimin okunma zamanı
  readBy?: string; // Bildirimi okuyan son kullanıcı ID'si
  isRead?: boolean; // Kullanıcı bazlı okunma durumu (/user endpoint'inden gelir)
  recipientCount?: number; // /list endpoint'inden gelen alıcı sayısı (UID listesi dönmez)
}

// Personel tipi
export interface Personnel {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  role?: string;
  department?: string;
  fcmToken?: string;
  companyId?: string;
  branchesId?: string;
}

// İsim getirme yardımcı fonksiyonu
export function getPersonFullName(person: Personnel): string {
  return `${person.firstName} ${person.lastName}`;
}

// Bildirim servisi sınıfı
class NotificationService {
  // Kullanıcının şirket ID'sini alma
  // 🔄 Cloud Functions üzerinden çeker, fallback olarak direkt Firebase
  async getCurrentUserCompany(userId: string): Promise<string | null> {
    try {
      // 🚀 ÖNCELİKLİ: Cloud Functions üzerinden çek
      console.log('[NotificationService] 📡 Cloud Functions ile kullanıcı şirketi çekiliyor...');
      const userData = await firebaseDataService.getUserData(userId);
      
      if (userData && userData.companyId) {
        console.log('[NotificationService] ✅ Cloud Functions ile companyId alındı:', userData.companyId);
        return userData.companyId;
      }
    } catch (cloudError) {
      console.warn('[NotificationService] ⚠️ Cloud Functions hatası, fallback kullanılıyor:', cloudError);
    }
    
    // 🔄 FALLBACK: Direkt Firebase
    try {
      const userRef = ref(database, `users/${userId}`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        const userData = snapshot.val();
        return userData.companyId || null;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  // Bildirim silme fonksiyonu — .NET notification API
  async deleteNotification(companyId: string, notificationId: string): Promise<boolean> {
    try {
      if (!companyId || !notificationId) {
        return false;
      }

      const resp = await notifApi.post('/api/notifications/delete', {
        Id: Number(notificationId),
        CompanyId: companyId,
      });
      return resp.data?.success === true;
    } catch (error) {
      return false;
    }
  }

  // Belirli bir personele gönderilen bildirimleri getir — .NET notification API
  async getPersonnelNotifications(companyId: string, personnelId: string): Promise<Notification[]> {
    try {
      if (!companyId || !personnelId) {
        return [];
      }

      const resp = await notifApi.get(
        `/api/notifications/user/${encodeURIComponent(personnelId)}?companyId=${encodeURIComponent(companyId)}`
      );

      if (!resp.data?.success || !Array.isArray(resp.data.data)) {
        return [];
      }

      const notifications: Notification[] = resp.data.data.map((n: any) =>
        this.mapApiNotification(n)
      );

      return notifications.sort((a, b) => b.createdAt - a.createdAt);
    } catch (error) {
      return [];
    }
  }

  // PascalCase API DTO'sunu camelCase Notification arayüzüne dönüştür
  private mapApiNotification(n: any): Notification {
    const statusRaw = (n.Status || '').toString().toLowerCase();
    const status: Notification['status'] =
      statusRaw === 'sent' ? 'sent' : statusRaw === 'failed' ? 'failed' : 'pending';

    return {
      id: String(n.Id),
      title: n.Title || '',
      body: n.Body || '',
      createdAt: n.CreatedAt ? new Date(n.CreatedAt).getTime() : Date.now(),
      // /list ve /user endpoint'leri alıcı/okuyan UID listesi dönmez — boş bırakılır
      recipients: [],
      read: [],
      status,
      sentCount: typeof n.SentCount === 'number' ? n.SentCount : undefined,
      errorCount: typeof n.ErrorCount === 'number' ? n.ErrorCount : undefined,
      sentAt: n.SentAt ? new Date(n.SentAt).getTime() : undefined,
      isRead: typeof n.IsRead === 'boolean' ? n.IsRead : undefined,
      readAt: n.ReadAt ? new Date(n.ReadAt).getTime() : undefined,
      recipientCount: typeof n.RecipientCount === 'number' ? n.RecipientCount : undefined,
    };
  }

  // Bildirimi oluştur + FCM gönder (tek çağrı) — .NET notification API
  // NOT: Bu tek çağrı hem kaydı kalıcı hale getirir HEM DE FCM gönderir.
  async sendNotification(
    companyId: string,
    userIds: string[],
    title: string,
    body: string
  ): Promise<NotificationStatus> {
    try {
      if (!companyId || userIds.length === 0) {
        return {
          success: false,
          message: 'Geçersiz şirket ID veya kullanıcı listesi',
        };
      }

      const resp = await notifApi.post('/api/notifications/create', {
        CompanyId: companyId,
        Title: title,
        Body: body,
        TargetUids: userIds,
      });

      const success = resp.data?.success === true;
      const send = resp.data?.data?.Send;
      const sentCount = send?.SuccessCount ?? 0;
      const errorCount = send?.FailureCount ?? 0;

      return {
        success,
        message: success
          ? `Bildirim başarıyla gönderildi (${sentCount} başarılı, ${errorCount} başarısız)`
          : 'Bildirim gönderilemedi',
        sentCount,
        errorCount,
      };
    } catch (error) {
      return {
        success: false,
        message: `Bildirim gönderilirken hata oluştu: ${error}`,
      };
    }
  }

  // Bildirimi okundu olarak işaretle — .NET notification API
  async markAsRead(firebaseUid: string, notificationId?: string): Promise<boolean> {
    try {
      if (!firebaseUid) {
        return false;
      }

      const resp = await notifApi.post('/api/notifications/mark-read', {
        FirebaseUid: firebaseUid,
        NotificationId: notificationId ? Number(notificationId) : undefined,
      });
      return resp.data?.success === true;
    } catch (error) {
      return false;
    }
  }

  // Kullanıcının okunmamış bildirim sayısını getir — .NET notification API
  async getUnreadCount(firebaseUid: string): Promise<number> {
    try {
      if (!firebaseUid) {
        return 0;
      }

      const resp = await notifApi.get(
        `/api/notifications/unread-count/${encodeURIComponent(firebaseUid)}`
      );
      return typeof resp.data?.unreadCount === 'number' ? resp.data.unreadCount : 0;
    } catch (error) {
      return 0;
    }
  }

  // Son gönderilen bildirimleri getir — .NET notification API
  async getRecentNotifications(companyId: string, limit = 10): Promise<Notification[]> {
    try {
      if (!companyId) {
        return [];
      }

      const resp = await notifApi.get(
        `/api/notifications/list?companyId=${encodeURIComponent(companyId)}&limit=${limit}`
      );

      if (!resp.data?.success || !Array.isArray(resp.data.data)) {
        return [];
      }

      const notifications: Notification[] = resp.data.data.map((n: any) =>
        this.mapApiNotification(n)
      );

      return notifications.sort((a, b) => b.createdAt - a.createdAt).slice(0, limit);
    } catch (error) {
      return [];
    }
  }

  // Performans metriklerine göre personel filtreleme
  async getPersonnelByPerformance(
    companyId: string, 
    isGoodPerformance: boolean
  ): Promise<Personnel[]> {
    try {
      // Tüm personeli al
      const allPersonnel = await this.getAllPersonnel(companyId);
      
      // İleri düzey veritabanı sorguları burada yapılacak
      // Şimdilik basit bir simulasyon yapıyoruz
      
      // Örnek: İyi performansı olanlar (id'nin son karakteri çift olanlar)
      // Kötü performansı olanlar (id'nin son karakteri tek olanlar)
      return allPersonnel.filter(person => {
        const lastChar = parseInt(person.id.slice(-1), 10);
        const isEven = lastChar % 2 === 0;
        return isGoodPerformance ? isEven : !isEven;
      });
    } catch (error) {
      return [];
    }
  }

  // Görevlerini kabul etmeyen personel listesi
  async getPersonnelWithUnacceptedTasks(companyId: string): Promise<Personnel[]> {
    try {
      // Tüm personeli al
      const allPersonnel = await this.getAllPersonnel(companyId);

      // Faz 5: Görevleri SQL'den al (taskDataService) — kabul edilmemiş (pending) görevler
      const unacceptedPersonnelIds = new Set<string>();
      let page = 1;
      const collected: any[] = [];
      for (;;) {
        const res = await taskDataService.listTasks({ status: 'pending', page, pageSize: 500 });
        collected.push(...res.items);
        if (res.items.length === 0 || collected.length >= (res.totalCount || 0)) break;
        page++;
      }
      collected.forEach(task => {
        if (task.status === 'pending' && task.personnelFirebaseUid) {
          unacceptedPersonnelIds.add(task.personnelFirebaseUid);
        }
      });

      // Kabul edilmemiş görevi olan personelleri filtrele
      return allPersonnel.filter(person => unacceptedPersonnelIds.has(person.id));
    } catch (error) {
      return [];
    }
  }

  // Personel listesini getir - Cloud Functions üzerinden
  async getAllPersonnel(companyId: string): Promise<Personnel[]> {
    try {
      // Cloud Functions üzerinden personel verilerini çek
      const personnelResult = await firebaseDataService.getPersonnel(companyId);
      
      if (!personnelResult || (Array.isArray(personnelResult) && personnelResult.length === 0)) {
        return [];
      }
      
      // Array ise objeye çevir
      let personnelData: Record<string, any> = {};
      if (Array.isArray(personnelResult)) {
        personnelResult.forEach((p: any) => { personnelData[p.id] = p; });
      } else {
        personnelData = personnelResult as Record<string, any>;
      }
      const usersData: Record<string, any> = {}; // Cloud Functions üzerinden ayrı ayrı çekilecek
      const personnelList: Personnel[] = [];
      
      // Veritabanı sonuçlarını Personnel nesnesine dönüştür
      for (const id in personnelData) {
        const person = personnelData[id];
        
        // ✅ Silinen personelleri filtrele
        const isDeleted = person.isDeleted === true;
        if (isDeleted) {
          continue;
        }
        
        // 🔧 FIX: users verisini cache'den al (ayrı sorgu yok!)
        const userData = usersData[id] || {};
        
        personnelList.push({
          id,
          firstName: person.firstName || userData.firstName || '',
          lastName: person.lastName || userData.lastName || '',
          email: userData.email || '',
          role: person.role || '',
          department: person.department || '',
          fcmToken: userData.fcmToken || '',
          companyId,
          branchesId: person.branchesId || ''
        });
      }
      
      return personnelList;
    } catch (error) {
      return [];
    }
  }
}

export default new NotificationService(); 